import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './Raffle.module.css';
import modalStyles from '../User/Modal.module.css';
import Modal from '../User/Modal';
import {
    TicketParticipants,
    raffleService,
} from '../../services/raffle.service';

const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getPeriodLabel = (period: string) => {
    const periodUpper = (period || '').toUpperCase();
    switch (periodUpper) {
        case 'DAILY':
            return 'Daily';
        case 'WEEKLY':
            return 'Weekly';
        case 'MONTHLY':
            return 'Monthly';
        default:
            return period;
    }
};

const RaffleDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [data, setData] = useState<TicketParticipants | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [drawing, setDrawing] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [result, setResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const fetchData = useCallback(async () => {
        const bonusTypeId = parseInt(id || '', 10);
        if (isNaN(bonusTypeId)) {
            setError('Invalid raffle ticket id');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await raffleService.getTicketParticipants(bonusTypeId);
            setData(result);
        } catch (err) {
            console.error('Error fetching ticket participants:', err);
            setError('Failed to load raffle ticket details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDrawWinner = async () => {
        const bonusTypeId = parseInt(id || '', 10);
        if (isNaN(bonusTypeId)) return;

        setConfirmOpen(false);
        setDrawing(true);
        try {
            const drawResult = await raffleService.drawTicketWinner(bonusTypeId);
            if (drawResult.winnerUserId) {
                setResult({
                    type: 'success',
                    text: `Winner drawn: User #${drawResult.winnerUserId} with ${drawResult.winnerTickets} ticket${drawResult.winnerTickets === 1 ? '' : 's'}.`,
                });
            } else {
                setResult({ type: 'success', text: 'Draw completed — no eligible winner found.' });
            }
            await fetchData();
        } catch (err: any) {
            console.error('Error drawing ticket winner:', err);
            const message =
                err?.graphQLErrors?.[0]?.message ||
                err?.message ||
                'Failed to draw winner. Please try again.';
            setResult({ type: 'error', text: message });
        } finally {
            setDrawing(false);
        }
    };

    const renderPrizeMeta = (prizeMeta: any) => {
        if (!prizeMeta) return null;
        let meta = prizeMeta;
        if (typeof prizeMeta === 'string') {
            try {
                meta = JSON.parse(prizeMeta);
            } catch (e) {
                return <span className={styles.infoValue}>{prizeMeta}</span>;
            }
        }
        if (typeof meta !== 'object') {
            return <span className={styles.infoValue}>{String(meta)}</span>;
        }
        return (
            <div className={styles.descriptionSection}>
                <div className={styles.descriptionLabel}>Prize</div>
                {Object.entries(meta).map(([key, value]) => (
                    <div key={key} className={styles.infoRow}>
                        <span className={styles.infoLabel}>{key}</span>
                        <span className={styles.infoValue}>
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <main className={styles.main}>
                <div className={styles.loading}>Loading raffle ticket details...</div>
            </main>
        );
    }

    if (error || !data) {
        return (
            <main className={styles.main}>
                <div className={styles.headerRow}>
                    <button onClick={() => navigate('/raffle')} className={styles.backBtn}>
                        ← Back
                    </button>
                </div>
                <div className={styles.noData}>{error || 'No data found'}</div>
            </main>
        );
    }

    const winner = data.winner;

    return (
        <main className={styles.main}>
            <div className={styles.headerRow}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={() => navigate('/raffle')} className={styles.backBtn}>
                        ← Back
                    </button>
                    <div>
                        <h1 className={styles.title} style={{ marginBottom: 0, textAlign: 'left' }}>
                            {data.bonusName}
                        </h1>
                        <div className={styles.raffleCode}>{data.bonusCode}</div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {!data.isDrawn && (
                        <button
                            className={styles.drawBtn}
                            onClick={() => setConfirmOpen(true)}
                            disabled={!data.isExpired || drawing}
                            title={
                                data.isExpired
                                    ? 'Draw the winner for this raffle'
                                    : 'Available once the raffle end date has passed'
                            }
                        >
                            {drawing ? 'Drawing…' : '🎲 Draw Winner'}
                        </button>
                    )}
                    <span
                        className={styles.typeChip}
                        style={{ backgroundColor: data.isExpired ? '#ef4444' : '#22c55e' }}
                    >
                        {data.isExpired ? 'Expired' : data.isActive ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </div>

            {/* Winner banner */}
            {winner ? (
                <div className={styles.winnerBanner}>
                    <div className={styles.winnerTrophy}>🏆</div>
                    <div className={styles.winnerInfo}>
                        <div className={styles.winnerLabel}>Winner</div>
                        <div className={styles.winnerName}>{winner.username}</div>
                        <div className={styles.winnerMeta}>
                            User #{winner.userId} · {winner.totalTickets} ticket
                            {winner.totalTickets === 1 ? '' : 's'} · Drawn {formatDate(winner.drawnAt)}
                        </div>
                    </div>
                </div>
            ) : (
                <div className={styles.pendingBanner}>
                    {data.isDrawn
                        ? 'Draw completed — no winner recorded'
                        : 'No winner yet — draw pending'}
                </div>
            )}

            {/* Summary stats */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <div className={styles.statValue}>{data.totalTickets}</div>
                    <div className={styles.statLabel}>Total Tickets Bought</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statValue}>{data.totalUsers}</div>
                    <div className={styles.statLabel}>Participants</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statValue}>${data.ticketPrice}</div>
                    <div className={styles.statLabel}>Ticket Price</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statValue}>
                        ${(data.ticketPrice * data.totalTickets).toLocaleString()}
                    </div>
                    <div className={styles.statLabel}>Total Collected</div>
                </div>
            </div>

            {/* Meta info */}
            <div className={styles.detailCard}>
                <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Period</span>
                    <span className={styles.infoValue}>{getPeriodLabel(data.period)}</span>
                </div>
                <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Start Date</span>
                    <span className={styles.infoValue}>{formatDate(data.startDate)}</span>
                </div>
                <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>End Date</span>
                    <span className={`${styles.infoValue} ${data.isExpired ? styles.expiredText : ''}`}>
                        {formatDate(data.endDate)}
                    </span>
                </div>
                <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Drawn</span>
                    <span className={styles.infoValue}>{data.isDrawn ? 'Yes' : 'No'}</span>
                </div>
                {renderPrizeMeta(data.prizeMeta)}
            </div>

            {/* Participants table */}
            <h2 className={styles.sectionHeading}>Participants ({data.participants.length})</h2>
            {data.participants.length === 0 ? (
                <div className={styles.noData}>No participants yet</div>
            ) : (
                <div className={styles.tableWrapper}>
                    <table className={styles.participantsTable}>
                        <thead>
                            <tr>
                                <th>User ID</th>
                                <th>Username</th>
                                <th style={{ textAlign: 'right' }}>Tickets</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.participants.map((p) => {
                                const isWinner = winner != null && p.userId === winner.userId;
                                return (
                                    <tr
                                        key={p.userId}
                                        className={isWinner ? styles.winnerRow : undefined}
                                    >
                                        <td>{p.userId}</td>
                                        <td>
                                            {p.username}
                                            {isWinner && (
                                                <span className={styles.winnerTag}>🏆 Winner</span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{p.totalTickets}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Draw winner confirmation popup */}
            <Modal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title="Draw Winner"
            >
                <p style={{ textAlign: 'center', marginBottom: 24 }}>
                    Draw the winner for <strong>{data.bonusName}</strong>? This cannot be undone.
                </p>
                <div className={modalStyles.actionRow}>
                    <button
                        className={modalStyles.cancelBtn}
                        onClick={() => setConfirmOpen(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className={modalStyles.saveBtn}
                        onClick={handleDrawWinner}
                        disabled={drawing}
                    >
                        {drawing ? 'Drawing…' : 'Draw Winner'}
                    </button>
                </div>
            </Modal>

            {/* Draw result popup */}
            <Modal
                isOpen={result !== null}
                onClose={() => setResult(null)}
                title={result?.type === 'error' ? 'Draw Failed' : 'Draw Complete'}
            >
                {result?.type === 'error' ? (
                    <div className={modalStyles.errorBox}>{result.text}</div>
                ) : (
                    <p style={{ textAlign: 'center', marginBottom: 24 }}>{result?.text}</p>
                )}
                <div className={modalStyles.actionRow}>
                    <button className={modalStyles.saveBtn} onClick={() => setResult(null)}>
                        OK
                    </button>
                </div>
            </Modal>
        </main>
    );
};

export default RaffleDetail;
